/**
 * O bloco do painel: escolhe o componente pelo `tipo` — §3.1 da spec #443.
 *
 * O contrato do painel deixou de ter um campo por seção e virou uma lista de blocos. Com isso
 * some a árvore de condições que `HomePage` mantinha para decidir qual seção existia: a decisão
 * de *existir* é do servidor, e aqui só se decide **com o quê desenhar**.
 *
 * Duas garantias que este arquivo carrega:
 *
 * - **Tipo desconhecido não derruba a tela.** Um bloco de formato que esta versão do navegador
 *   ainda não conhece simplesmente não aparece — o resto do painel continua de pé.
 * - **Conteúdo fora do formato também não.** `conteudo` chega como `unknown`, e cada ramo só
 *   entrega o componente depois de conferir a forma do que recebeu.
 */
import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import {
  Biotech,
  Business,
  CalendarMonth,
  CalendarToday,
  Description,
  EventNote,
  Groups,
  HowToReg,
  PersonOff,
  PrecisionManufacturing,
  ReportProblem,
  School,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import type {
  AttendanceSummary,
  BlocoMontado,
  DashboardAlert,
  DashboardStatCard,
  FilaParada,
  ScheduleOccupancy,
  TipoDeBloco,
  TodayAppointment,
  WhatsAppDashboardStatus,
} from '../../../types/dashboard';
import AlertsList from './AlertsList';
import AttendanceFunnel from './AttendanceFunnel';
import CanalDeWhatsApp from './CanalDeWhatsApp';
import DashboardStatTile from './DashboardStatTile';
import FilaDeMensagens from './FilaDeMensagens';
import ScheduleOccupancyCard from './ScheduleOccupancyCard';
import TodaySchedule from './TodaySchedule';

/** O nome do ícone que o servidor manda no cartão → o componente do MUI. */
const ICONES: Record<string, React.ReactNode> = {
  Business: <Business sx={{ fontSize: 28 }} />,
  Groups: <Groups sx={{ fontSize: 28 }} />,
  EventNote: <EventNote sx={{ fontSize: 28 }} />,
  Description: <Description sx={{ fontSize: 28 }} />,
  School: <School sx={{ fontSize: 28 }} />,
  ReportProblem: <ReportProblem sx={{ fontSize: 28 }} />,
  HowToReg: <HowToReg sx={{ fontSize: 28 }} />,
  PersonOff: <PersonOff sx={{ fontSize: 28 }} />,
  CalendarToday: <CalendarToday sx={{ fontSize: 28 }} />,
  PrecisionManufacturing: <PrecisionManufacturing sx={{ fontSize: 28 }} />,
  CalendarMonth: <CalendarMonth sx={{ fontSize: 28 }} />,
  Biotech: <Biotech sx={{ fontSize: 28 }} />,
};

export interface LarguraDoBloco {
  xs: number;
  sm?: number;
  md: number;
}

const LARGURA_PADRAO: LarguraDoBloco = { xs: 12, md: 6 };

const LARGURAS: Partial<Record<TipoDeBloco, LarguraDoBloco>> = {
  cartao: { xs: 12, sm: 6, md: 3 },
  canal: { xs: 12, md: 4 },
  fila: { xs: 12, md: 4 },
};

/** Quanto o bloco ocupa na grade. Tipo que ainda não se conhece cabe na largura padrão. */
export const larguraDoBloco = (tipo: TipoDeBloco): LarguraDoBloco =>
  LARGURAS[tipo] ?? LARGURA_PADRAO;

/**
 * Ordena pelo peso decrescente.
 *
 * O servidor já entrega ordenado, e mesmo assim a tela ordena de novo: o peso é o contrato, e
 * depender da ordem do JSON é depender de uma garantia que ninguém escreveu.
 */
export const ordenarBlocos = (blocos: readonly BlocoMontado[]): BlocoMontado[] =>
  [...blocos].sort((a, b) => b.peso - a.peso);

const comoObjeto = <T,>(conteudo: unknown): T | null =>
  typeof conteudo === 'object' && conteudo !== null && !Array.isArray(conteudo)
    ? (conteudo as T)
    : null;

const comoLista = <T,>(conteudo: unknown): T[] | null =>
  Array.isArray(conteudo) ? (conteudo as T[]) : null;

/**
 * A caixa que faz a altura da linha chegar ao card.
 *
 * Os cards já pedem a altura inteira do que os segura; o que faltava era alguém com altura para
 * dar. Esta caixa recebe a altura do item da linha e a repassa ao card, que continua acomodando
 * o conteúdo dele a partir do topo — o que estica é a caixa, não o texto de dentro.
 */
export const ESTICA_O_CARTAO = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  minWidth: 0,
  '& > *': { flex: 1 },
} satisfies SxProps<Theme>;

type IrPara = (destino?: string) => void;

/** O componente do formato, sem a caixa: `null` quando o formato ou o conteúdo não se conhece. */
const cartaoDoBloco = (
  bloco: BlocoMontado,
  loading: boolean | undefined,
  irPara: IrPara
): React.ReactElement | null => {
  const { tipo, rotulo, caminho, conteudo } = bloco;

  switch (tipo) {
    case 'cartao': {
      const cartao = comoObjeto<DashboardStatCard>(conteudo);
      if (!cartao) return null;
      return (
        <DashboardStatTile
          icon={ICONES[cartao.icon] || <EventNote sx={{ fontSize: 28 }} />}
          total={cartao.total}
          label={cartao.label}
          color={cartao.color}
          onClick={() => irPara(cartao.link ?? caminho)}
        />
      );
    }

    case 'lista': {
      const alertas = comoLista<DashboardAlert>(conteudo);
      if (!alertas) return null;
      return (
        <AlertsList
          rotulo={rotulo}
          alerts={alertas}
          loading={loading}
          onAlertClick={(alerta) => irPara(alerta.link)}
        />
      );
    }

    case 'funil': {
      const resumo = comoObjeto<AttendanceSummary>(conteudo);
      if (!resumo) return null;
      return <AttendanceFunnel data={resumo} loading={loading} />;
    }

    case 'medidor': {
      const ocupacao = comoObjeto<ScheduleOccupancy>(conteudo);
      if (!ocupacao) return null;
      return <ScheduleOccupancyCard data={ocupacao} loading={loading} />;
    }

    case 'tabela': {
      const agenda = comoLista<TodayAppointment>(conteudo);
      if (!agenda) return null;
      return (
        <TodaySchedule
          appointments={agenda}
          loading={loading}
          onViewAll={caminho ? () => irPara(caminho) : undefined}
        />
      );
    }

    case 'canal': {
      const canal = comoObjeto<WhatsAppDashboardStatus>(conteudo);
      if (!canal) return null;
      return (
        <CanalDeWhatsApp
          rotulo={rotulo}
          data={canal}
          onGerenciar={caminho ? () => irPara(caminho) : undefined}
        />
      );
    }

    case 'fila': {
      const fila = comoObjeto<FilaParada>(conteudo);
      if (!fila) return null;
      return (
        <FilaDeMensagens
          rotulo={rotulo}
          data={fila}
          onVerFila={caminho ? () => irPara(caminho) : undefined}
        />
      );
    }

    default:
      return null;
  }
};

interface BlocoDoPainelProps {
  bloco: BlocoMontado;
  loading?: boolean;
}

const BlocoDoPainel: React.FC<BlocoDoPainelProps> = ({ bloco, loading }) => {
  const navigate = useNavigate();

  const irPara = (destino?: string) => {
    if (destino) navigate(destino);
  };

  const cartao = cartaoDoBloco(bloco, loading, irPara);
  if (!cartao) return null;

  return (
    <Box data-testid="cartao-esticado" sx={ESTICA_O_CARTAO}>
      {cartao}
    </Box>
  );
};

export default BlocoDoPainel;
